import dayjs from "dayjs"

import { APICode } from "@/api/APICode"
import { BaseResponse } from "@/api/base"
import api from "@/api/common"
import trackingPage from "@/api/tracking-page"
import TPAPI from "@/api/tracking-page/index"
import {
  FeaturesText,
  ITrackingPageOptions,
  Status,
  Translations,
} from "@/models/ITrackingPageOptions"
import { initModulesStore, registerModuleListener } from "@/pages/TrackingPage/EstimatedDeliveryTime/listener"
import stores from "@/stores"
import TrackingPageUtils from "@/utils/TrackingPageUtils"

let initOptions: ITrackingPageOptions

/**
 * 清理 translations 空文本属性（与 TrackingPage 内逻辑一致）
 */
export function filterEmptyFeatureTextTrans(initOpts: Pick<ITrackingPageOptions, "translations" | "translations_config">) {
  if (!initOpts.translations) {
    return
  }

  const primaryLocale = initOpts.translations_config?.primary_locale || "en"
  const _primaryTransFeaturesText = initOpts.translations[primaryLocale].features_text as FeaturesText

  const isEmptyAdditionalTextAbove = (_primaryTransFeaturesText.additional_text_above === "")
  const isEmptyAdditionalTextBelow = (_primaryTransFeaturesText.additional_text_below === "")
  const isEmptyCustomTrackingInfo = (_primaryTransFeaturesText.custom_tracking_info === "")
  const customStatusEmptyData = _primaryTransFeaturesText.custom_status.map(({ info, status }) => ({
    status: status === "",
    info: info === "",
  }))

  const langs = Object.keys(initOpts.translations)

  langs.forEach((lang) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const featuresText: any = {}

    if (!isEmptyAdditionalTextAbove) {
      featuresText.additional_text_above = (initOpts.translations as Translations)[lang].features_text.additional_text_above
    }
    if (!isEmptyAdditionalTextBelow) {
      featuresText.additional_text_below = (initOpts.translations as Translations)[lang].features_text.additional_text_below
    }
    if (!isEmptyCustomTrackingInfo) {
      featuresText.custom_tracking_info = (initOpts.translations as Translations)[lang].features_text.custom_tracking_info
    }
    featuresText.custom_status = customStatusEmptyData.map(({ info, status }, i) => {
      if (info && status) {
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusData: any = {}

      if (!info) {
        statusData.info = (initOpts.translations as Translations)[lang].features_text.custom_status[i].info
      }
      if (!status) {
        statusData.status = (initOpts.translations as Translations)[lang].features_text.custom_status[i].status
      }

      return statusData
    })
      .filter(_ => _) as Status[]

    (initOpts.translations as Translations)[lang].features_text = featuresText
  })
}

/**
 * 拉取跟踪页配置（TPAPI.baseInfo 等），供 TrackingPage 与 App Window 子路由共用。
 * 已加载过则跳过，避免重复请求。
 */
export const fetchTrackingPageBaseData = () => {
  const { trackingPageStore } = stores

  if (trackingPageStore.loadedDataTime > 0) {
    return
  }

  TPAPI.baseInfo()
    .then(({ data: response, status }) => {
      interface Response extends BaseResponse {
        tracking_page_config: ITrackingPageOptions
      }

      const res: Response = response

      if (status === APICode.OK) {
        initOptions = res.tracking_page_config

        filterEmptyFeatureTextTrans(initOptions)

        TrackingPageUtils.setInitOptions(initOptions)
        trackingPageStore.setBaseOptions(initOptions)
        trackingPageStore.setLoadedDataTime(dayjs().unix())
        TrackingPageUtils.registerListener()

        initModulesStore()
        registerModuleListener()
      }
    })

  // Return / Trust 安装状态只用于 Order lookup widget 卡片里的勾选项，
  // 白名单模式（超管后台 + 客户未授权）下那张卡不展示，跳过这两个请求
  if (stores.userInfoSore.isAdminScoped) {
    return
  }

  try {
    void Promise.allSettled([
      api.getReturnInstall(),
      api.getTrustInstall(),
    ]).then(([returnResult, trustResult]) => {
      if (returnResult.status === "fulfilled") {
        trackingPageStore.setReturnInstalled(returnResult.value.data.status.is_installed)
      }

      if (trustResult.status === "fulfilled") {
        trackingPageStore.setTrustInstalled(trustResult.value.data.status.is_installed)
      }
    })
  } catch (e) {
    console.log(e)
  }
}

/**
 * 产品集合（与 TrackingPage.getProductCollection 一致）
 */
export const fetchTrackingPageProductCollects = () => {
  const { productCollection } = stores

  // tracking-page/collects 只服务于 Product recommendation，
  // 白名单模式（超管后台 + 客户未授权）下该模块不展示，不必请求
  if (stores.userInfoSore.isAdminScoped) {
    return
  }

  trackingPage.productCollects()
    .then(({ data: response }) => {
      const { data } = response

      const productCollections = [{
        label: "Select...",
        value: "0",
      }]

      if (Array.isArray(data)) {
        data.map((item: { title: string; id: number }) => {
          productCollections.push({
            label: item.title,
            value: `${item.id}`,
          })
        })
      }

      productCollection.setProductCollection(productCollections)
    })
}
